import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "../components/Button";
import { Loader } from "../components/Loader";
import { formatInteger } from "../helpers/formatNumber";
import { formatCopenhagenDateTime } from "../helpers/dateFilters";
import {
  getPromoCodeClaims,
  getPromoCodePools,
  PromoCodeClaim,
  PromoCodePoolListItem,
} from "../api/promoCodes";

const PAGE_SIZE = 50;

const AdminPromoCodes: React.FC = () => {
  const [pools, setPools] = useState<PromoCodePoolListItem[]>([]);
  const [claims, setClaims] = useState<PromoCodeClaim[]>([]);
  const [total, setTotal] = useState(0);
  const [initialLoad, setInitialLoad] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  // URL is the single source of truth for the selected pool, applied search and page.
  const appliedSearch = searchParams.get("search") ?? "";
  const selectedBusinessId = searchParams.get("business") ?? "";
  const pageRaw = parseInt(searchParams.get("page") ?? "1", 10);
  const pageNumber = Number.isFinite(pageRaw) && pageRaw >= 1 ? pageRaw : 1;

  const [search, setSearch] = useState(appliedSearch);

  useEffect(() => {
    setSearch(searchParams.get("search") ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const buildParams = (
    businessId: string,
    searchValue: string,
    page: number
  ): URLSearchParams => {
    const params = new URLSearchParams();
    if (businessId) params.set("business", businessId);
    const trimmed = searchValue.trim();
    if (trimmed) params.set("search", trimmed);
    if (page > 1) params.set("page", String(page));
    return params;
  };

  useEffect(() => {
    let ignore = false;
    const run = async () => {
      try {
        const poolList = await getPromoCodePools();
        if (ignore) return;
        setPools(poolList);
        // No pool selected yet — pick the first one so the page is useful immediately.
        if (!selectedBusinessId && poolList.length > 0) {
          setSearchParams(buildParams(poolList[0].businessContentfulId, "", 1), {
            replace: true,
          });
        }
        setError(null);
      } catch (err) {
        if (ignore) return;
        setError("Failed to fetch promo code pools");
        console.error("Error fetching promo code pools:", err);
      } finally {
        if (!ignore) setInitialLoad(false);
      }
    };
    run();
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedBusinessId) return;
    let ignore = false;
    const run = async () => {
      try {
        setLoading(true);
        const response = await getPromoCodeClaims(selectedBusinessId, {
          search: appliedSearch || undefined,
          pageNumber,
          pageSize: PAGE_SIZE,
        });
        if (ignore) return;
        setClaims(response.data);
        setTotal(response.total);
        setError(null);
      } catch (err) {
        if (ignore) return;
        setError("Failed to fetch promo code claims");
        console.error("Error fetching promo code claims:", err);
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    run();
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  if (initialLoad) {
    return (
      <div className="loading-page">
        <Loader />
      </div>
    );
  }

  const selectedPool = pools.find(
    (p) => p.businessContentfulId === selectedBusinessId
  );
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <div className="flex flex-col gap-4 mb-10">
        <h2 className="font-medium">Promo codes</h2>

        {pools.length === 0 ? (
          <div className="text-gray-500">No promo code pools imported yet.</div>
        ) : (
          <div className="flex flex-wrap gap-4">
            {pools.map((pool) => {
              const isSelected =
                pool.businessContentfulId === selectedBusinessId;
              return (
                <button
                  key={pool.businessContentfulId}
                  type="button"
                  onClick={() =>
                    setSearchParams(
                      buildParams(pool.businessContentfulId, "", 1)
                    )
                  }
                  className={`bg-white rounded-lg shadow p-4 min-w-[220px] text-left border-2 ${
                    isSelected ? "border-purple-500" : "border-transparent"
                  }`}
                >
                  <div className="text-gray-500 text-sm">
                    {pool.businessName ?? pool.businessContentfulId}
                  </div>
                  <div className="text-2xl font-semibold mt-1">
                    {formatInteger(pool.claimed)}
                    <span className="text-base font-normal text-gray-500">
                      {" "}
                      / {formatInteger(pool.total)} claimed
                    </span>
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    {formatInteger(pool.remaining)} remaining
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {selectedPool && (
          <form
            className="flex items-end gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              setSearchParams(buildParams(selectedBusinessId, search, 1));
            }}
          >
            <div className="w-[360px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search (code, email, name)
              </label>
              <input
                type="text"
                className="w-full p-2 border border-gray-300 rounded-md"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="e.g. OISTERBONUS… or an email"
              />
            </div>
            <div className="flex items-center gap-3">
              <Button type="submit">Apply</Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSearch("");
                  setSearchParams(buildParams(selectedBusinessId, "", 1));
                }}
              >
                Reset
              </Button>
            </div>
          </form>
        )}
      </div>

      {loading && (
        <div className="flex justify-center my-8">
          <Loader />
        </div>
      )}

      {error && <div className="error mb-4">{error}</div>}

      {selectedPool && (
        <>
          <div className="overflow-x-auto">
            <table className="table-default">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Claimed at</th>
                  <th>Name</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {claims.length === 0 && !loading ? (
                  <tr>
                    <td colSpan={4} className="text-center text-gray-500">
                      {appliedSearch
                        ? "No claims match this search."
                        : "No codes claimed yet."}
                    </td>
                  </tr>
                ) : (
                  claims.map((claim) => (
                    <tr key={claim.code}>
                      <td className="whitespace-nowrap font-mono">
                        {claim.code}
                      </td>
                      <td className="whitespace-nowrap">
                        {formatCopenhagenDateTime(claim.claimedAt)}
                      </td>
                      <td className="whitespace-nowrap">{claim.name ?? "—"}</td>
                      <td className="whitespace-nowrap">
                        {claim.email ?? "Account deleted"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="flex justify-between items-center mt-4">
            <Button
              onClick={() =>
                setSearchParams(
                  buildParams(
                    selectedBusinessId,
                    appliedSearch,
                    Math.max(1, pageNumber - 1)
                  )
                )
              }
              disabled={pageNumber === 1}
              variant="outline"
            >
              Previous
            </Button>
            <span>
              Page {pageNumber} of {totalPages} · {formatInteger(total)} claims
            </span>
            <Button
              onClick={() =>
                setSearchParams(
                  buildParams(selectedBusinessId, appliedSearch, pageNumber + 1)
                )
              }
              disabled={pageNumber >= totalPages}
              variant="outline"
            >
              Next
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminPromoCodes;
